import { PDFDocument, StandardFonts, rgb, degrees } from 'pdf-lib';

// Watermark styling constants (matching server-side)
export const WATERMARK_COLOR = rgb(0.75, 0.75, 0.75);
export const WATERMARK_OPACITY = 0.25;
export const WATERMARK_FONT_SIZE = 30;

/**
 * Adds a repeated watermark pattern to all pages of a PDF
 * Matches the server-side implementation in stories-service/lib/pdfMerge.ts
 *
 * @param pdfData - The PDF as ArrayBuffer or Uint8Array
 * @param watermarkText - The text to display as watermark
 * @param margins - Optional margins for watermark placement
 * @returns The modified PDF as Uint8Array
 */
export async function addWatermarkToPdf(pdfData: ArrayBuffer | Uint8Array, watermarkText: string): Promise<Uint8Array> {
  // Load the PDF document
  const pdfDoc = await PDFDocument.load(pdfData);
  // Embed font
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const pages = pdfDoc.getPages();

  for (const page of pages) {
    const { width, height } = page.getSize();

    const fontSize = WATERMARK_FONT_SIZE;
    const angle = degrees(45);
    const stepX = 280; // Horizontal spacing
    const stepY = 350; // Vertical spacing

    for (let y = -height; y < height * 2; y += stepY) {
      for (let x = -width; x < width * 2; x += stepX) {
        page.drawText(watermarkText, {
          x,
          y,
          size: fontSize,
          font,
          color: WATERMARK_COLOR,
          rotate: angle,
          opacity: WATERMARK_OPACITY
        });
      }
    }
  }

  // Save and return the modified PDF
  return await pdfDoc.save();
}

/**
 * Adds watermark to PDF and triggers download
 * Uses the same format as the existing downloadPdfFromBlob function
 *
 * @param pdfString - The PDF as a string (from API response.pdf)
 * @param watermarkText - The text to display as watermark (null to skip)
 * @param fileName - The name for the downloaded file
 * @param margins - Optional margins for watermark placement
 */
export async function addWatermarkAndDownload(
  pdfResponse: { type: 'Buffer'; data: number[] },
  watermarkText: string | null,
  fileName: string
): Promise<void> {
  let finalPdfBytes: Uint8Array = new Uint8Array(pdfResponse.data);
  // Only add watermark if text is provided
  if (watermarkText) {
    finalPdfBytes = await addWatermarkToPdf(finalPdfBytes, watermarkText);
  }
  // Create blob and trigger download using file-saver (same as existing)
  // Cast buffer to satisfy TypeScript's strict typing
  const blob = new Blob([finalPdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' });

  // Use dynamic import for file-saver to match existing pattern
  type FileSaverModule = {
    default?: (data: Blob, filename: string) => void;
    saveAs?: (data: Blob, filename: string) => void;
  };
  const fileSaver: FileSaverModule = await import('file-saver');
  const saveAs = fileSaver.default ?? fileSaver.saveAs;
  if (saveAs) {
    saveAs(blob, `${fileName}.pdf`);
  }
}
